import React, { PureComponent } from 'react'
import { isMerchandiseLineOpen } from './openMerchandiseLines'

const DEFAULT_ESTIMATE = 240
const DEFAULT_OVERSCAN = 5
const MIN_FALLBACK_VIEWPORT = 360
const UNMEASURED_FLOOR = 80
const REMEASURE_DELAYS_MS = [0, 32, 120, 320, 700]
const FAST_SCROLL_DELTA = 64
const FAST_SCROLL_TRAVEL = 200
const FAST_SCROLL_WINDOW_MS = 100
const SCROLL_IDLE_MS = 150
const MAX_EXTRA_OVERSCAN = 10
const HYDRATE_PER_FRAME_SCROLL = 2
const HYDRATE_PER_FRAME_IDLE = 4
const LINE_ANCHOR_PREFIX = 'adcvdtggt'
const registeredLists = new Set()
const PGA_PIN_MS = 1000
let pgaScrollPin = null
let programmaticScrollDepth = 0

export function isProgrammaticFormScroll() {
  return programmaticScrollDepth > 0
}

function markProgrammaticScroll() {
  programmaticScrollDepth += 1
  window.requestAnimationFrame(() => {
    window.requestAnimationFrame(() => {
      programmaticScrollDepth = Math.max(0, programmaticScrollDepth - 1)
    })
  })
}

function formScroller() {
  return document.getElementsByClassName('formContentarea_bc')[0] || null
}

/**
 * mode "keep": hold the PGA title at its current viewport top (expand / title collapse).
 * mode "reveal": if the title is outside the form pane (Done at the bottom of a long PGA),
 * move it to the top of the pane. If it is already visible, leave it.
 */
export function beginPgaScrollPin(el, mode) {
  if (!el || typeof el.getBoundingClientRect !== 'function') return
  pgaScrollPin = {
    el,
    mode: mode === 'reveal' ? 'reveal' : 'keep',
    top: el.getBoundingClientRect().top,
    until: Date.now() + PGA_PIN_MS,
  }
  const apply = () => applyPgaScrollPin(formScroller())
  window.requestAnimationFrame(() => {
    apply()
    window.requestAnimationFrame(apply)
  })
  ;[50, 150, 350, 750].forEach((ms) => window.setTimeout(apply, ms))
}

export function applyPgaScrollPin(scroller) {
  const pin = pgaScrollPin
  if (!pin || !scroller || Date.now() > pin.until) return 0
  if (!pin.el || !pin.el.isConnected) return 0
  const scrollerRect = scroller.getBoundingClientRect()
  const elRect = pin.el.getBoundingClientRect()
  let delta = 0
  if (pin.mode === 'keep') {
    delta = elRect.top - pin.top
  } else if (
    elRect.top < scrollerRect.top + 8 ||
    elRect.bottom > scrollerRect.bottom - 8
  ) {
    delta = elRect.top - (scrollerRect.top + 8)
  }
  if (Math.abs(delta) <= 2) return 0
  markProgrammaticScroll()
  const windowX = window.scrollX
  const windowY = window.scrollY
  scroller.scrollTop += delta
  window.scrollTo(windowX, windowY)
  registeredLists.forEach((list) => {
    if (!list) return
    if (pin.mode === 'reveal' && typeof list.cacheListOrigin === 'function') {
      list.cacheListOrigin()
    } else if (list.listOrigin != null) {
      list.listOrigin += delta
    }
    if (typeof list.scheduleUpdate === 'function') list.scheduleUpdate()
  })
  return delta
}

function registerVirtualList(instance) {
  registeredLists.add(instance)
}

function unregisterVirtualList(instance) {
  registeredLists.delete(instance)
}

function merchandiseIdFromTarget(targetName) {
  if (!targetName || typeof targetName !== 'string') return null
  if (targetName.indexOf(LINE_ANCHOR_PREFIX) !== 0) return null
  const id = targetName.slice(LINE_ANCHOR_PREFIX.length)
  if (!id || id === 'GrossWeight') return null
  return id
}

function tryScrollRegisteredLists(merchandiseId) {
  let scrolled = false
  registeredLists.forEach((list) => {
    if (list && typeof list.scrollToKey === 'function' && list.scrollToKey(merchandiseId)) {
      scrolled = true
    }
  })
  return scrolled
}

function waitForSelector(selector, timeoutMs = 2000) {
  return new Promise((resolve) => {
    const existing = document.querySelector(selector)
    if (existing) {
      resolve(existing)
      return
    }
    const started = Date.now()
    const tick = () => {
      const el = document.querySelector(selector)
      if (el) {
        resolve(el)
        return
      }
      if (Date.now() - started >= timeoutMs) {
        resolve(null)
        return
      }
      window.setTimeout(tick, 40)
    }
    tick()
  })
}

/**
 * Off-screen virtualized lines are unmounted, so warning links cannot
 * querySelector / scrollIntoView until the row is pinned and scrolled in.
 */
export function ensureVirtualLineMounted(targetName, options = {}) {
  const merchandiseId = merchandiseIdFromTarget(targetName)
  if (!merchandiseId) return Promise.resolve(false)

  const selector = 'a[name="' + targetName + '"]'
  const waitForList = options.waitForList === true

  const attempt = () => {
    if (tryScrollRegisteredLists(merchandiseId)) return 'scrolled'
    if (document.querySelector(selector)) return 'present'
    return null
  }

  const first = attempt()
  if (first === 'scrolled') return waitForSelector(selector).then(Boolean)
  if (first === 'present') return Promise.resolve(true)
  if (!waitForList && registeredLists.size === 0) return Promise.resolve(false)

  return new Promise((resolve) => {
    const started = Date.now()
    const tick = () => {
      const result = attempt()
      if (result === 'scrolled') {
        waitForSelector(selector).then((el) => resolve(!!el))
        return
      }
      if (result === 'present') {
        resolve(true)
        return
      }
      const elapsed = Date.now() - started
      if (!waitForList && registeredLists.size === 0 && elapsed >= 250) {
        resolve(false)
        return
      }
      if (elapsed >= 2000) {
        resolve(false)
        return
      }
      window.setTimeout(tick, 40)
    }
    tick()
  })
}

function scrollWithinForm(element) {
  if (!element) return
  const scroller =
    document.querySelector('.formContentarea_bc') || findScrollParent(element)
  if (!scroller || scroller === document.documentElement || scroller === document.body) {
    return
  }
  const windowX = window.scrollX
  const windowY = window.scrollY
  const scrollerRect = scroller.getBoundingClientRect()
  const elementRect = element.getBoundingClientRect()
  scroller.scrollTop += elementRect.top - scrollerRect.top - 8
  window.scrollTo(windowX, windowY)
}

export function jumpToMerchandiseWarning(warning) {
  if (!warning || !warning.targetName_c) return Promise.resolve(false)

  const invoiceToggleLink = warning.invoice
    ? document.getElementById('plus-' + warning.invoice)
    : null
  const invoiceWasCollapsed = !!invoiceToggleLink
  if (invoiceToggleLink) invoiceToggleLink.click()

  return ensureVirtualLineMounted(warning.targetName_c, {
    waitForList: invoiceWasCollapsed,
  }).then(() => {
    const anchor = document.querySelector('a[name="' + warning.targetName_c + '"]')
    if (anchor) {
      scrollWithinForm(anchor)
    }
    const toggleLink = document.getElementById('plus-' + warning.targetName_c)
    if (toggleLink) toggleLink.click()
    return !!anchor
  })
}

/**
 * Variable-height virtualization against an ancestor scroller.
 *
 * Line items contain nested collapsibles (line body, duties, PGA accordions)
 * whose height changes after the first paint — including async PGA form fetch.
 * Do not wrap this in AutoSizer (parent height is auto → 0 → blank screen).
 * Do not pin row minHeight to the last measurement (collapse would not shrink).
 */
function findScrollParent(node) {
  let el = node
  while (el && el !== document.body) {
    if (el.classList && el.classList.contains('formContentarea_bc')) {
      return el
    }
    el = el.parentElement
  }
  el = node
  while (el && el !== document.body) {
    const style = window.getComputedStyle(el)
    const overflowY = style && style.overflowY
    if (overflowY === 'auto' || overflowY === 'scroll') {
      return el
    }
    el = el.parentElement
  }
  return document.scrollingElement || document.documentElement
}

function readRowHeight(el) {
  if (!el) return 0
  // Use the border box only. scrollHeight with overflow:visible can stay tall
  // after collapse (or include descendants) and leave multi-hundred-px gaps
  // between collapsed line items — which then bounce when remasured on scroll.
  const rectHeight = el.getBoundingClientRect().height
  const offsetHeight = el.offsetHeight || 0
  return Math.ceil(Math.max(rectHeight, offsetHeight))
}

class AutoSizedVirtualList extends PureComponent {
  static defaultProps = {
    estimatedItemSize: DEFAULT_ESTIMATE,
    overscanCount: DEFAULT_OVERSCAN,
    itemKey: (index) => index,
  }

  constructor(props) {
    super(props)
    this.rootRef = React.createRef()
    this.heightsByKey = Object.create(null)
    this.rowObservers = Object.create(null)
    this.rowMutations = Object.create(null)
    this.rowListeners = Object.create(null)
    this.rowEls = Object.create(null)
    this.rowTimeouts = Object.create(null)
    this.measureRefCache = Object.create(null)
    this.offsets = [0]
    this.scrollParent = null
    this.rafUpdate = null
    this.rafScroll = null
    this.rafMeasure = Object.create(null)
    this.pinnedIndex = null
    this.unpinTimer = null
    this.scrollIdleTimer = null
    this.extraOverscan = 0
    this.velocityWindow = []
    this.measuredSum = 0
    this.measuredCount = 0
    this.hydratedIndexes = new Set()
    this.hasSeededHydration = false
    this.hydrateRaf = null
    this.scrolling = false
    this.listOrigin = null
    this._correctingScroll = false
    this.state = {
      rangeVersion: 0,
      isScrolling: false,
    }
    this.metrics = {
      startOffset: 0,
      viewportHeight: this.getFallbackViewport(),
    }
  }

  getFallbackViewport = () => {
    if (typeof window === 'undefined') return MIN_FALLBACK_VIEWPORT
    return Math.max(window.innerHeight - 180, MIN_FALLBACK_VIEWPORT)
  }

  getKey = (index) => {
    return this.props.itemKey(index)
  }

  getEstimateForIndex = (index) => {
    const { estimatedItemSize } = this.props
    const estimate =
      typeof estimatedItemSize === 'function'
        ? estimatedItemSize(index)
        : estimatedItemSize
    return estimate > 0 ? estimate : DEFAULT_ESTIMATE
  }

  getSize = (index) => {
    const measured = this.heightsByKey[this.getKey(index)]
    if (measured > 0) return measured
    // Do not use a live average. Expanding/collapsing one PGA would
    // resize unmeasured rows above this one and jump the open PGA.
    return this.getEstimateForIndex(index)
  }

  /**
   * Keep the list's DOM height in sync with measured offsets before adjusting
   * scroll. Otherwise scrollHeight is still the pre-expand value and the
   * browser / maxScroll clamp eats the correction — the open line jumps.
   */
  syncListDomHeight = () => {
    const list = this.rootRef && this.rootRef.current
    if (!list) return
    const totalHeight = this.getTotalHeight()
    if (totalHeight > 0) {
      list.style.height = `${totalHeight}px`
    }
  }

  /**
   * When a row fully above the viewport changes height, nudge scrollTop by
   * the same delta so visible rows stay put. Do not touch listOrigin —
   * startOffset must track scrollTop (scrollTop - listOrigin).
   */
  correctScrollForSizeDelta = (delta) => {
    const parent = this.scrollParent
    if (!parent || !delta) return
    this._correctingScroll = true
    try {
      this.syncListDomHeight()
      // Do not clamp to a pre-layout maxScroll — that truncates expand
      // corrections and shifts the focused line toward the bottom.
      markProgrammaticScroll()
      parent.scrollTop = Math.max(0, (parent.scrollTop || 0) + delta)
      if (this.listOrigin != null) {
        this.metrics = {
          startOffset: (parent.scrollTop || 0) - this.listOrigin,
          viewportHeight:
            this.metrics.viewportHeight ||
            parent.clientHeight ||
            this.getFallbackViewport(),
        }
      } else if (this.metrics) {
        this.metrics = {
          ...this.metrics,
          startOffset: (this.metrics.startOffset || 0) + delta,
        }
      }
    } finally {
      this._correctingScroll = false
    }
  }

  resetMeasuredHeights = () => {
    this.heightsByKey = Object.create(null)
    this.measuredSum = 0
    this.measuredCount = 0
  }

  /**
   * Expand/Collapse All changes estimatedItemSize in the same render as line
   * bodies open/close. Clear stale measurements before offsets are built so
   * absolute rows do not keep short (collapsed) tops and overlap.
   */
  syncEstimateHeights = () => {
    const { estimatedItemSize } = this.props
    const estimateToken =
      typeof estimatedItemSize === 'function' ? 'fn' : estimatedItemSize
    if (this._estimateToken === estimateToken) return
    this._estimateToken = estimateToken
    this.resetMeasuredHeights()
  }

  rebuildOffsets = () => {
    this.syncEstimateHeights()
    const { itemCount } = this.props
    const offsets = new Array(itemCount + 1)
    offsets[0] = 0
    for (let i = 0; i < itemCount; i++) {
      offsets[i + 1] = offsets[i] + this.getSize(i)
    }
    this.offsets = offsets
    return offsets
  }

  getTotalHeight = () => {
    const offsets = this.offsets || this.rebuildOffsets()
    return offsets[this.props.itemCount] || 0
  }

  findStartIndex = (scrollOffset) => {
    const offsets = this.offsets || this.rebuildOffsets()
    const { itemCount } = this.props
    if (itemCount <= 0) return 0
    let low = 0
    let high = itemCount - 1
    const target = Math.max(0, scrollOffset)
    while (low < high) {
      const mid = (low + high) >> 1
      if (offsets[mid + 1] <= target) {
        low = mid + 1
      } else {
        high = mid
      }
    }
    return low
  }

  findIndexByKey = (key) => {
    const { itemCount, itemKey } = this.props
    const target = String(key)
    for (let i = 0; i < itemCount; i++) {
      if (String(itemKey(i)) === target) return i
    }
    return -1
  }

  scrollToKey = (key) => {
    const index = this.findIndexByKey(key)
    if (index < 0) return false
    return this.scrollToIndex(index)
  }

  scrollToIndex = (index) => {
    if (index < 0 || index >= this.props.itemCount) return false
    this.pinnedIndex = index
    this.rebuildOffsets()
    const list = this.rootRef.current
    if (list) {
      const parent = this.scrollParent || findScrollParent(list)
      this.bindScrollParent(parent)
      this.readMetrics()
      const targetOffset = this.offsets[index] || 0
      if (parent) {
        const windowX = window.scrollX
        const windowY = window.scrollY
        parent.scrollTop += targetOffset - (this.metrics.startOffset || 0)
        window.scrollTo(windowX, windowY)
      }
      this.readMetrics()
    }
    this.setState((s) => ({ rangeVersion: s.rangeVersion + 1 }))
    if (this.unpinTimer) window.clearTimeout(this.unpinTimer)
    this.unpinTimer = window.setTimeout(() => {
      this.pinnedIndex = null
      this.unpinTimer = null
      this.scheduleUpdate()
    }, 2500)
    return true
  }

  getVisibleRange = () => {
    const { itemCount, overscanCount } = this.props
    if (itemCount <= 0) {
      return { start: 0, end: -1 }
    }
    this.rebuildOffsets()
    const viewportHeight = Math.max(
      this.metrics.viewportHeight || 0,
      MIN_FALLBACK_VIEWPORT
    )
    const overscan = (overscanCount || DEFAULT_OVERSCAN) + (this.extraOverscan || 0)
    const viewStart = this.metrics.startOffset
    const viewEnd = viewStart + viewportHeight
    let start = this.findStartIndex(viewStart)
    let end = this.findStartIndex(Math.max(viewStart, viewEnd))
    start = Math.max(0, start - overscan)
    end = Math.min(itemCount - 1, end + overscan)
    // Never render an empty window when items exist — that is a blank screen.
    if (end < start || Number.isNaN(start) || Number.isNaN(end)) {
      start = 0
      end = Math.min(itemCount - 1, Math.max(overscanCount, 2))
    }
    return { start, end }
  }

  getStrictVisibleRange = () => {
    const { itemCount } = this.props
    if (itemCount <= 0) return { start: 0, end: -1 }
    this.rebuildOffsets()
    const viewportHeight = Math.max(
      this.metrics.viewportHeight || 0,
      MIN_FALLBACK_VIEWPORT
    )
    const viewStart = this.metrics.startOffset
    const viewEnd = viewStart + viewportHeight
    const start = this.findStartIndex(viewStart)
    const end = this.findStartIndex(Math.max(viewStart, viewEnd))
    return {
      start: Math.max(0, start),
      end: Math.min(itemCount - 1, end),
    }
  }

  pruneHydrated = () => {
    const { start, end } = this.getVisibleRange()
    const next = new Set()
    this.hydratedIndexes.forEach((index) => {
      if (index >= start && index <= end) next.add(index)
    })
    if (this.pinnedIndex != null) next.add(this.pinnedIndex)
    // Keep expanded lines hydrated so scroll/prune does not remount them and
    // remeasure from the collapsed estimate (that scroll-jumps the open line).
    if (!this.props.expandAllLines) {
      const { itemCount } = this.props
      for (let index = 0; index < itemCount; index++) {
        if (isMerchandiseLineOpen(this.getKey(index))) next.add(index)
      }
    }
    this.hydratedIndexes = next
  }

  collectHydrateQueue = (viewportOnly) => {
    const { itemCount } = this.props
    if (itemCount <= 0) return []
    const strict = this.getStrictVisibleRange()
    const range = this.getVisibleRange()
    const seen = Object.create(null)
    const queue = []
    const consider = (index) => {
      if (index < 0 || index >= itemCount) return
      if (this.hydratedIndexes.has(index) || seen[index]) return
      seen[index] = true
      queue.push(index)
    }
    const mid = (strict.start + strict.end) >> 1
    consider(mid)
    const span = Math.max(strict.end - strict.start, 1)
    for (let distance = 1; distance <= span + 1; distance++) {
      consider(mid - distance)
      consider(mid + distance)
    }
    if (!viewportOnly) {
      for (let index = range.start; index <= range.end; index++) {
        consider(index)
      }
    }
    if (this.pinnedIndex != null) consider(this.pinnedIndex)
    return queue
  }

  hydrateIndexes = (indexes) => {
    if (!indexes || !indexes.length) return false
    indexes.forEach((index) => this.hydratedIndexes.add(index))
    return true
  }

  cancelHydration = () => {
    if (!this.hydrateRaf) return
    window.cancelAnimationFrame(this.hydrateRaf)
    this.hydrateRaf = null
  }

  startHydration = () => {
    if (this.scrolling || this.hydrateRaf) return
    this.hydrateRaf = window.requestAnimationFrame(() => {
      this.hydrateRaf = null
      if (this.scrolling) return
      const queue = this.collectHydrateQueue(false)
      if (!queue.length) return
      this.hydrateIndexes(queue.slice(0, HYDRATE_PER_FRAME_IDLE))
      this.setState((s) => ({ rangeVersion: s.rangeVersion + 1 }))
      if (queue.length > HYDRATE_PER_FRAME_IDLE) this.startHydration()
    })
  }

  seedVisibleHydration = (budget) => {
    const queue = this.collectHydrateQueue(true)
    if (!queue.length) return
    this.hydrateIndexes(queue.slice(0, budget || HYDRATE_PER_FRAME_SCROLL))
  }

  captureHeight = (index, el) => {
    const node = el || this.rowEls[index]
    if (!node) return
    const height = readRowHeight(node)
    // 0 means the node is not laid out yet. Storing it would collapse the row.
    if (height < 1) return
    const key = this.getKey(index)
    if (this.heightsByKey[key] === height) return
    const prev = this.heightsByKey[key]
    const lineOpen = isMerchandiseLineOpen(key)
    // While scrolling, still accept growth (Expand All / PGA open) so absolute
    // rows do not keep a short offset and paint on top of each other. Defer
    // small shrinks until scroll settles — but always shrink collapsed lines
    // (stale tall heights are what create the empty gaps / bounce).
    if (
      (this.scrolling || this.state.isScrolling) &&
      prev > 0 &&
      height < prev &&
      lineOpen &&
      height > prev * 0.5
    ) {
      return
    }
    const oldTop = (this.offsets && this.offsets[index]) || 0
    const prevSize = prev > 0 ? prev : this.getEstimateForIndex(index)
    const startOffset = (this.metrics && this.metrics.startOffset) || 0
    const viewportHeight = Math.max(
      (this.metrics && this.metrics.viewportHeight) || 0,
      MIN_FALLBACK_VIEWPORT
    )
    const visualTop = node.getBoundingClientRect().top
    if (prev > 0) {
      this.measuredSum -= prev
      this.measuredCount -= 1
    }
    this.heightsByKey[key] = height
    this.measuredSum += height
    this.measuredCount += 1
    this.rebuildOffsets()
    this.syncListDomHeight()
    const delta = height - prevSize
    const parent = this.scrollParent
    const isScrolling = this.scrolling || this.state.isScrolling
    if (parent && delta) {
      // Prefer index vs viewport start — more stable than comparing bottoms
      // when startOffset/listOrigin is briefly stale during expand.
      const startIndex = this.findStartIndex(startOffset)
      if (index < startIndex) {
        // Row fully above the viewport: always correct scroll (including while
        // scrolling). Without this, Expand All + jump-bottom + scroll-up makes
        // visible lines bounce as overscan rows remasure.
        this.correctScrollForSizeDelta(delta)
      } else if (
        !isScrolling &&
        oldTop < startOffset + viewportHeight &&
        oldTop + prevSize > startOffset
      ) {
        // Idle + visible: pin this row's visual top across the React commit
        // (line expand / PGA open). Immediate getBoundingClientRect after
        // rebuildOffsets is still pre-paint and under-corrects.
        const anchorTop = visualTop
        const pinRow = () => {
          if (!node.isConnected) return
          const shift = node.getBoundingClientRect().top - anchorTop
          if (Math.abs(shift) > 1) {
            this.correctScrollForSizeDelta(shift)
          }
          applyPgaScrollPin(parent)
        }
        pinRow()
        window.requestAnimationFrame(() => {
          pinRow()
          window.requestAnimationFrame(pinRow)
        })
      }
    }
    this.scheduleUpdate()
  }

  scheduleMeasure = (index, el, delay = 0) => {
    const run = () => this.captureHeight(index, el)
    if (delay <= 0) {
      if (this.rafMeasure[index]) return
      this.rafMeasure[index] = window.requestAnimationFrame(() => {
        this.rafMeasure[index] = null
        run()
      })
      return
    }
    if (!this.rowTimeouts[index]) this.rowTimeouts[index] = []
    this.rowTimeouts[index].push(window.setTimeout(run, delay))
  }

  remesureAfterLayoutChange = (index, el) => {
    REMEASURE_DELAYS_MS.forEach((delay) => this.scheduleMeasure(index, el, delay))
  }

  clearRowTimers = (index) => {
    if (this.rafMeasure[index]) {
      window.cancelAnimationFrame(this.rafMeasure[index])
      this.rafMeasure[index] = null
    }
    const timeouts = this.rowTimeouts[index]
    if (timeouts) {
      timeouts.forEach((id) => window.clearTimeout(id))
      this.rowTimeouts[index] = []
    }
  }

  disconnectRow = (index) => {
    this.clearRowTimers(index)
    const observer = this.rowObservers[index]
    if (observer) {
      observer.disconnect()
      this.rowObservers[index] = null
    }
    const mutation = this.rowMutations[index]
    if (mutation) {
      mutation.disconnect()
      this.rowMutations[index] = null
    }
    const el = this.rowEls[index]
    const listeners = this.rowListeners[index]
    if (el && listeners) {
      el.removeEventListener('transitionend', listeners.transition)
      el.removeEventListener('animationend', listeners.animation)
      el.removeEventListener('click', listeners.click, true)
    }
    this.rowEls[index] = null
    this.rowListeners[index] = null
  }

  bindRow = (index, el) => {
    this.disconnectRow(index)
    if (!el) return
    this.rowEls[index] = el

    if (typeof ResizeObserver !== 'undefined') {
      const observer = new ResizeObserver(() => this.captureHeight(index, el))
      observer.observe(el)
      this.rowObservers[index] = observer
    }

    // PGA / duty / accordion content mounts, toggles class, or fetches after click.
    if (typeof MutationObserver !== 'undefined') {
      const mutation = new MutationObserver(() => {
        if (this.scrolling) return
        this.scheduleMeasure(index, el)
      })
      mutation.observe(el, {
        childList: true,
        subtree: true,
      })
      this.rowMutations[index] = mutation
    }

    const onLayoutEvent = () => this.remesureAfterLayoutChange(index, el)
    const listeners = {
      transition: onLayoutEvent,
      animation: onLayoutEvent,
      click: onLayoutEvent,
    }
    el.addEventListener('transitionend', listeners.transition)
    el.addEventListener('animationend', listeners.animation)
    el.addEventListener('click', listeners.click, true)
    this.rowListeners[index] = listeners

    this.captureHeight(index, el)
    this.remesureAfterLayoutChange(index, el)
  }

  getMeasureRef = (index) => {
    if (!this.measureRefCache[index]) {
      this.measureRefCache[index] = (el) => this.bindRow(index, el)
    }
    return this.measureRefCache[index]
  }

  scheduleUpdate = () => {
    if (this.rafUpdate) return
    this.rafUpdate = window.requestAnimationFrame(() => {
      this.rafUpdate = null
      this.setState((s) => ({ rangeVersion: s.rangeVersion + 1 }))
    })
  }

  cacheListOrigin = () => {
    const list = this.rootRef.current
    if (!list) return
    const parent = this.scrollParent || findScrollParent(list)
    this.bindScrollParent(parent)
    if (!parent) return
    const parentRect = parent.getBoundingClientRect
      ? parent.getBoundingClientRect()
      : { top: 0 }
    const listRect = list.getBoundingClientRect()
    this.listOrigin = (parent.scrollTop || 0) - (parentRect.top - listRect.top)
    this.metrics.viewportHeight = Math.max(
      parent.clientHeight || parentRect.height || 0,
      this.getFallbackViewport()
    )
  }

  readMetrics = () => {
    const list = this.rootRef.current
    if (!list) return
    const parent = findScrollParent(list)
    this.bindScrollParent(parent)
    const parentRect = parent.getBoundingClientRect
      ? parent.getBoundingClientRect()
      : { top: 0 }
    const listRect = list.getBoundingClientRect()
    const startOffset = parentRect.top - listRect.top
    this.listOrigin = (parent.scrollTop || 0) - startOffset
    const viewportHeight = Math.max(
      parent.clientHeight || parentRect.height || 0,
      this.getFallbackViewport()
    )
    this.metrics = {
      startOffset,
      viewportHeight,
    }
  }

  readMetricsFast = () => {
    const parent = this.scrollParent
    if (!parent || this.listOrigin == null) {
      this.readMetrics()
      return
    }
    this.metrics = {
      startOffset: (parent.scrollTop || 0) - this.listOrigin,
      viewportHeight:
        this.metrics.viewportHeight ||
        parent.clientHeight ||
        this.getFallbackViewport(),
    }
  }

  isOwnScrollEvent = (event) => {
    if (!event || event.type !== 'scroll') return true
    const target = event.target
    if (!this.scrollParent) return true
    return (
      target === this.scrollParent ||
      target === document ||
      target === document.scrollingElement ||
      target === document.documentElement
    )
  }

  armScrollIdle = () => {
    if (this.scrollIdleTimer) window.clearTimeout(this.scrollIdleTimer)
    this.scrollIdleTimer = window.setTimeout(() => {
      this.scrollIdleTimer = null
      this.scrolling = false
      this.extraOverscan = 0
      this.velocityWindow = []
      this.cacheListOrigin()
      if (this.state.isScrolling) {
        this.setState((s) => ({
          rangeVersion: s.rangeVersion + 1,
          isScrolling: false,
        }))
      }
      this.hydratedIndexes.forEach((index) => {
        this.scheduleMeasure(index, this.rowEls[index])
      })
      this.startHydration()
    }, SCROLL_IDLE_MS)
  }

  recordScrollVelocity = (offset) => {
    const now = Date.now()
    this.velocityWindow.push({ t: now, offset })
    this.velocityWindow = this.velocityWindow.filter(
      (sample) => now - sample.t < FAST_SCROLL_WINDOW_MS
    )
    if (this.velocityWindow.length < 2) return 0
    return Math.abs(
      this.velocityWindow[this.velocityWindow.length - 1].offset -
        this.velocityWindow[0].offset
    )
  }

  onScrollOrResize = (event) => {
    if (event && event.type === 'resize') {
      this.listOrigin = null
      this.readMetrics()
      this.scheduleUpdate()
      return
    }
    if (!this.isOwnScrollEvent(event)) return
    if (this._correctingScroll) {
      this.readMetricsFast()
      return
    }
    if (isProgrammaticFormScroll()) {
      this.readMetricsFast()
      return
    }

    this.scrolling = true
    this.cancelHydration()
    this.readMetricsFast()
    const nextOffset = this.metrics.startOffset || 0
    const travel = this.recordScrollVelocity(nextOffset)
    if (travel > 40) {
      this.extraOverscan = Math.min(
        MAX_EXTRA_OVERSCAN,
        Math.max(this.extraOverscan, 4 + Math.ceil(travel / 80))
      )
    }
    this.pruneHydrated()
    // Keep a few viewport rows hydrated while scrolling so Expand All does
    // not flash empty placeholders (without mounting every open line).
    this.seedVisibleHydration(
      travel > FAST_SCROLL_TRAVEL ? 1 : HYDRATE_PER_FRAME_SCROLL
    )
    if (!this.state.isScrolling) {
      this.setState((s) => ({
        rangeVersion: s.rangeVersion + 1,
        isScrolling: true,
      }))
    } else {
      this.scheduleUpdate()
    }
    this.armScrollIdle()
  }

  bindScrollParent = (parent) => {
    if (!parent || this.scrollParent === parent) return
    if (this.scrollParent) {
      this.scrollParent.removeEventListener('scroll', this.onScrollOrResize)
    }
    this.scrollParent = parent
    parent.addEventListener('scroll', this.onScrollOrResize, { passive: true })
  }

  attachScrollParent = () => {
    const list = this.rootRef.current
    if (!list) return
    this.bindScrollParent(findScrollParent(list))
    this.readMetrics()
    this.cacheListOrigin()
    this.scheduleUpdate()
  }

  componentDidMount() {
    registerVirtualList(this)
    this.attachScrollParent()
    window.addEventListener('resize', this.onScrollOrResize)
    // Scroll does not bubble. Capture on document so `.formContentarea_bc`
    // scrolling always refreshes the visible window (avoids a blank gap).
    document.addEventListener('scroll', this.onScrollOrResize, {
      passive: true,
      capture: true,
    })
  }

  componentDidUpdate(prevProps) {
    if (prevProps.resetToken !== this.props.resetToken) {
      this.resetMeasuredHeights()
      this.hydratedIndexes = new Set()
      this.hasSeededHydration = false
      this._estimateToken = undefined
    }
    // After Expand All, line bodies open in a follow-up commit. Remeasure
    // mounted rows once that layout lands (do not measure in the same tick as
    // the estimate change — rows may still be collapsed and would re-store
    // short heights).
    if (prevProps.estimatedItemSize !== this.props.estimatedItemSize) {
      window.setTimeout(() => {
        Object.keys(this.rowEls).forEach((indexKey) => {
          const index = Number(indexKey)
          const el = this.rowEls[index]
          if (el) this.remesureAfterLayoutChange(index, el)
        })
        this.scheduleUpdate()
      }, 0)
    }
    if (!this.scrollParent) {
      this.attachScrollParent()
    }
  }

  componentWillUnmount() {
    unregisterVirtualList(this)
    if (this.unpinTimer) window.clearTimeout(this.unpinTimer)
    if (this.scrollIdleTimer) window.clearTimeout(this.scrollIdleTimer)
    if (this.hydrateRaf) window.cancelAnimationFrame(this.hydrateRaf)
    if (this.rafUpdate) window.cancelAnimationFrame(this.rafUpdate)
    if (this.rafScroll) window.cancelAnimationFrame(this.rafScroll)
    Object.keys(this.rafMeasure).forEach((key) => {
      if (this.rafMeasure[key]) window.cancelAnimationFrame(this.rafMeasure[key])
    })
    window.removeEventListener('resize', this.onScrollOrResize)
    document.removeEventListener('scroll', this.onScrollOrResize, { capture: true })
    if (this.scrollParent) {
      this.scrollParent.removeEventListener('scroll', this.onScrollOrResize)
    }
    Object.keys(this.rowEls).forEach((key) => this.disconnectRow(key))
  }

  render() {
    const { itemCount, renderItem } = this.props
    this.rebuildOffsets()
    const totalHeight = Math.max(this.getTotalHeight(), itemCount > 0 ? this.getSize(0) : 1)
    const { start, end } = this.getVisibleRange()
    if (!this.hasSeededHydration && itemCount > 0) {
      for (let index = start; index <= end; index++) {
        this.hydratedIndexes.add(index)
      }
      this.hasSeededHydration = true
    }
    // Expand All: never placeholder the strict viewport (bounce reads as
    // lines jumping when a gray shell swaps for a full line item).
    if (this.props.expandAllLines) {
      const strict = this.getStrictVisibleRange()
      for (let index = strict.start; index <= strict.end; index++) {
        this.hydratedIndexes.add(index)
      }
    }
    const items = []
    const renderIndexes = []
    for (let index = start; index <= end; index++) {
      renderIndexes.push(index)
    }
    if (
      this.pinnedIndex != null &&
      this.pinnedIndex >= 0 &&
      this.pinnedIndex < itemCount &&
      (this.pinnedIndex < start || this.pinnedIndex > end)
    ) {
      renderIndexes.push(this.pinnedIndex)
      this.hydratedIndexes.add(this.pinnedIndex)
    }
    // Expanded lines stay mounted outside the window so scroll-away
    // does not remount them collapsed (line body and PGA).
    // Skip when Expand All is on — mounting every line defeats virtualization
    // and remasure storms make rows bounce while scrolling.
    if (!this.props.expandAllLines) {
      for (let index = 0; index < itemCount; index++) {
        if (!isMerchandiseLineOpen(this.getKey(index))) continue
        this.hydratedIndexes.add(index)
        if (renderIndexes.indexOf(index) === -1) renderIndexes.push(index)
      }
    }
    for (let i = 0; i < renderIndexes.length; i++) {
      const index = renderIndexes[i]
      const key = this.getKey(index)
      const measured = this.heightsByKey[key]
      const showPlaceholder =
        !this.hydratedIndexes.has(index) && this.pinnedIndex !== index
      const rowHeight = this.getSize(index)
      items.push(
        <div
          key={key}
          ref={showPlaceholder ? undefined : this.getMeasureRef(index)}
          data-virtual-index={index}
          style={{
            position: 'absolute',
            top: this.offsets[index],
            left: 0,
            width: '100%',
            display: 'flow-root',
            overflow: 'visible',
            overflowAnchor: 'none',
            // Placeholders reserve the estimate slot. Live rows use a small
            // floor only — tying minHeight to the estimate poisons the first
            // measure at ~240px and leaves permanent gaps between collapsed lines.
            minHeight: showPlaceholder
              ? rowHeight
              : measured > 0
              ? undefined
              : UNMEASURED_FLOOR,
          }}
        >
          {showPlaceholder ? (
            <div
              className="virtual-line-placeholder"
              style={{
                minHeight: rowHeight,
                boxSizing: 'border-box',
                margin: '6px 0',
                padding: '10px 12px',
                border: '1px solid #dce3ec',
                borderRadius: 4,
                background: '#f4f7fa',
                color: '#6b7c93',
                fontSize: 13,
                lineHeight: '20px',
              }}
            >
              Line {index + 1}
            </div>
          ) : (
            renderItem({ index })
          )}
        </div>
      )
    }

    return (
      <div
        ref={this.rootRef}
        className="auto-sized-virtual-list"
        style={{
          position: 'relative',
          width: '100%',
          height: totalHeight,
          overflow: 'visible',
          overflowAnchor: 'none',
          minHeight: itemCount > 0 ? this.getSize(0) : undefined,
        }}
      >
        {items}
      </div>
    )
  }
}

export default AutoSizedVirtualList
